import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShieldCheck, CheckCircle, WarningCircle, XCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicVerification() {
  const [searchParams] = useSearchParams();
  const [variableNumber, setVariableNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const numberParam = searchParams.get('number');
    if (numberParam) {
      setVariableNumber(numberParam);
      handleVerify(numberParam);
    }
  }, []);

  const handleVerify = async (number = variableNumber) => {
    if (!number.trim()) {
      toast.error('Please enter a variable number');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const { data } = await axios.post(`${API_URL}/api/verify`, {
        variable_number: number.trim()
      });
      setResult(data);
      if (data.status === 'success') {
        toast.success('Product authenticated successfully!');
      } else if (data.status === 'already_authenticated') {
        toast.warning('Product already authenticated');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Verification failed';
      setResult({ status: 'error', message: errorMessage });
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleVerify();
  };

  const getStatusIcon = () => {
    if (!result) return null;
    if (result.status === 'success') {
      return <CheckCircle size={64} weight="duotone" className="text-green-600" />;
    } else if (result.status === 'already_authenticated') {
      return <WarningCircle size={64} weight="duotone" className="text-amber-600" />;
    } else {
      return <XCircle size={64} weight="duotone" className="text-red-600" />;
    }
  };

  const getStatusColor = () => {
    if (!result) return '';
    if (result.status === 'success') return 'border-green-200 bg-green-50';
    if (result.status === 'already_authenticated') return 'border-amber-200 bg-amber-50';
    return 'border-red-200 bg-red-50';
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Pane - Architectural Image with Glassmorphism */}
      <div
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/20578685/pexels-photo-20578685.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)'
        }}
      >
        <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl"></div>
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-xl border border-white/40 rounded-xl mb-6">
            <ShieldCheck size={40} weight="duotone" />
          </div>
          <h1 className="text-5xl font-bold tracking-tight leading-tight mb-4">
            Verify Product<br />Authenticity
          </h1>
          <p className="text-lg text-white/90 leading-relaxed max-w-md">
            Enter your product's variable number or scan the QR code to verify its authenticity instantly.
          </p>
        </div>
      </div>

      {/* Right Pane - Verification Form */}
      <div className="w-full lg:w-1/2 bg-white flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-900 rounded-xl mb-4">
              <ShieldCheck size={32} weight="duotone" className="text-white" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">Product Verification</h1>
            <p className="mt-2 text-slate-600">Verify product authenticity</p>
          </div>

          {/* Verification Form */}
          <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="variableNumber" className="text-slate-900">
                  Insert Number
                </Label>
                <Input
                  id="variableNumber"
                  type="text"
                  placeholder="Enter product number"
                  value={variableNumber}
                  onChange={(e) => setVariableNumber(e.target.value)}
                  required
                  data-testid="variable-number-input"
                  className="font-mono focus:ring-2 focus:ring-slate-900 text-lg"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-slate-900 hover:bg-slate-800 active:scale-95 transition-transform duration-200 py-6 text-lg"
                disabled={loading}
                data-testid="verify-submit-button"
              >
                {loading ? 'Verifying...' : 'Verify Product'}
              </Button>
            </form>

            {/* Result Display */}
            {result && (
              <div className={`mt-6 p-6 rounded-xl border-2 transition-all duration-300 ${getStatusColor()}`} data-testid="verification-result">
                <div className="flex flex-col items-center text-center space-y-4">
                  {getStatusIcon()}
                  <div>
                    <h3 className="text-xl font-bold tracking-tight text-slate-900 mb-2">
                      {result.status === 'success' && 'Genuine Product'}
                      {result.status === 'already_authenticated' && 'Already Authenticated'}
                      {result.status === 'error' && 'Verification Failed'}
                    </h3>
                    <p className="text-slate-700 leading-relaxed">{result.message}</p>
                  </div>

                  {result.product && (
                    <div className="w-full mt-4 pt-4 border-t border-slate-300 space-y-2 text-left">
                      <div>
                        <span className="text-sm font-medium text-slate-600">Product Name:</span>
                        <p className="text-slate-900 font-medium">{result.product.product_name}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-slate-600">Description:</span>
                        <p className="text-slate-900">{result.product.description}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-slate-600">Variable Number:</span>
                        <p className="text-slate-900 font-mono text-sm">{result.product.variable_number}</p>
                      </div>
                      {result.authenticated_at && (
                        <div>
                          <span className="text-sm font-medium text-slate-600">First Authenticated:</span>
                          <p className="text-slate-900 text-sm">
                            {new Date(result.authenticated_at).toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 text-center text-sm text-slate-600">
            <a href="/login" className="text-blue-600 hover:text-blue-700 font-medium">
              Admin Login →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
